import type { IconName } from "./Icon";

export type NavItemKey =
  | "recents"
  | "branches"
  | "favorites"
  | "chat"
  | "personal"
  | "connectors"
  | "skills"
  | "monitoring";

export type NavRule = "always" | "when-badged" | "hidden";

// The nav's two fixed groups (divider-separated in the sidebar); items reorder
// only within their own section, never across.
export type NavSection = "main" | "manage";
export const NAV_SECTIONS: readonly NavSection[] = ["main", "manage"];

export type NavItemDef = {
  key: NavItemKey;
  label: string;
  icon: IconName;
  section: NavSection;
  // Whether "Show when badged" is offered; only rows with a count/dot source.
  hasBadge: boolean;
  // Never rendered on mobile, regardless of the user's rule.
  desktopOnly?: boolean;
};

export type NavConfigEntry = { item: NavItemDef; rule: NavRule };

export const NAV_ITEMS: ReadonlyArray<NavItemDef> = [
  {
    key: "recents",
    label: "Recents",
    icon: "history",
    section: "main",
    hasBadge: false,
  },
  {
    key: "branches",
    label: "Branches",
    icon: "git-branch",
    section: "main",
    hasBadge: true,
  },
  {
    key: "favorites",
    label: "Favorites",
    icon: "heart",
    section: "main",
    hasBadge: false,
  },
  { key: "chat", label: "Chat", icon: "ask", section: "main", hasBadge: true },
  {
    key: "personal",
    label: "Personal workflows",
    icon: "circle-user-round",
    section: "main",
    hasBadge: false,
  },
  {
    key: "monitoring",
    label: "Monitoring",
    icon: "scan-heart",
    section: "manage",
    hasBadge: false,
    desktopOnly: true,
  },
  {
    key: "connectors",
    label: "Connectors",
    icon: "api-key-custom",
    section: "manage",
    hasBadge: false,
    desktopOnly: true,
  },
  {
    key: "skills",
    label: "Skills",
    icon: "graduation-cap",
    section: "manage",
    hasBadge: false,
    desktopOnly: true,
  },
];

const DEFAULT_RULE: NavRule = "always";
const VALID_RULES: ReadonlySet<string> = new Set([
  "always",
  "when-badged",
  "hidden",
]);

function normalizeRule(item: NavItemDef, raw: string | undefined): NavRule {
  if (!raw || !VALID_RULES.has(raw)) return DEFAULT_RULE;
  const rule = raw as NavRule;
  if (rule === "when-badged" && !item.hasBadge) return DEFAULT_RULE;
  return rule;
}

// Parses the persisted `key:rule` list into an ordered list of items, keeping
// known keys in saved order, dropping unknown keys, and appending any items the
// user hasn't seen yet (in canonical order) so a newly added nav item shows up
// automatically. Mirrors resolveWorkflowTabOrder in WorkflowTabs.ts.
export function resolveNavConfig(
  saved: string | null | undefined
): ReadonlyArray<NavConfigEntry> {
  const byKey = new Map(NAV_ITEMS.map((item) => [item.key, item]));
  const result: NavConfigEntry[] = [];
  if (saved) {
    for (const token of saved.split(",")) {
      const [rawKey, rawRule] = token.split(":");
      const key = rawKey?.trim();
      const item = key ? byKey.get(key as NavItemKey) : undefined;
      if (item) {
        result.push({ item, rule: normalizeRule(item, rawRule?.trim()) });
        byKey.delete(item.key);
      }
    }
  }
  for (const item of NAV_ITEMS) {
    if (byKey.has(item.key)) result.push({ item, rule: DEFAULT_RULE });
  }
  return result;
}

export function serializeNavConfig(
  entries: ReadonlyArray<NavConfigEntry>
): string {
  return entries.map((e) => `${e.item.key}:${e.rule}`).join(",");
}

export const DEFAULT_NAV_CONFIG = serializeNavConfig(
  NAV_ITEMS.map((item) => ({ item, rule: DEFAULT_RULE }))
);

export function applyVisibilityRule(rule: NavRule, isBadged: boolean): boolean {
  if (rule === "hidden") return false;
  if (rule === "when-badged") return isBadged;
  return true;
}

export function sectionEntries(
  entries: ReadonlyArray<NavConfigEntry>,
  section: NavSection
): NavConfigEntry[] {
  return entries.filter((e) => e.item.section === section);
}

// Moves an item to a new position within its own section, then rebuilds the
// full ordered list section-by-section (so sections stay contiguous). Order
// across sections is fixed by NAV_SECTIONS.
export function moveWithinSection(
  entries: ReadonlyArray<NavConfigEntry>,
  key: NavItemKey,
  toIndex: number
): NavConfigEntry[] {
  const target = entries.find((e) => e.item.key === key);
  if (!target) return entries.slice();
  const section = target.item.section;
  const rest = sectionEntries(entries, section).filter(
    (e) => e.item.key !== key
  );
  const clamped = Math.min(Math.max(0, toIndex), rest.length);
  rest.splice(clamped, 0, target);
  return NAV_SECTIONS.flatMap((s) =>
    s === section ? rest : sectionEntries(entries, s)
  );
}
